import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import BillingService from '../../services/BillingService';
import ClinicSettingsService from '../../services/ClinicSettingsService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';

const BillingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const parseApiDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  
  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // In a real application, you would fetch from your API
        // For now, we'll use mock data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Fetch real data from API
        const response = await BillingService.getInvoiceById(id);
        const invoice = response.data || response;

        const paymentsResponse = await BillingService.getPaymentsByInvoiceId(id);
        const payment = paymentsResponse.data && paymentsResponse.data.length > 0 ? paymentsResponse.data[0] : null;

        const invoiceDate = parseApiDate(invoice.invoiceDate || invoice.InvoiceDate);
        const paymentDate = parseApiDate(
          payment ? (payment.PaymentDate || payment.paymentDate) : (invoice.paymentDate || invoice.PaymentDate)
        );
        
        // Transform the data to match the expected format
        const billingData = {
          id: invoice.invoiceId || invoice.InvoiceId,
          patientId: invoice.patientId || invoice.PatientId || '',
          patientName: invoice.patient 
            ? `${(invoice.patient.firstName || invoice.patient.FirstName || '')} ${(invoice.patient.lastName || invoice.patient.LastName || '')}`.trim() || 'Unknown Patient'
            : (invoice.Patient ? `${invoice.Patient.FirstName} ${invoice.Patient.LastName}` : 'Unknown Patient'),
          date: invoiceDate,
          invoiceNumber: invoice.invoiceNumber || invoice.InvoiceNumber || '',
          description: invoice.description || invoice.Description || 'Medical Services',
          amount: invoice.totalAmount || invoice.TotalAmount || 0,
          status: invoice.status || invoice.Status || 'Pending',
          paymentMethod: payment ? (payment.PaymentMethod || payment.paymentMethod) : (invoice.paymentMethod || invoice.PaymentMethod || null),
          paymentDate: paymentDate,
          items: (invoice.invoiceItems || invoice.InvoiceItems || []).map(item => ({
            id: item.invoiceItemId || item.InvoiceItemId || Date.now(),
            description: item.notes || item.Notes || 
                        item.billingItem?.itemName || item.billingItem?.ItemName || 
                        item.BillingItem?.itemName || item.BillingItem?.ItemName || 
                        item.BillingItem?.Description || 'Unknown Item',
            quantity: item.quantity || item.Quantity || 1,
            unitPrice: item.unitPrice || item.UnitPrice || 0,
            discount: item.discount || item.Discount || 0,
            subtotal: item.subtotal || item.Subtotal || (((item.quantity || item.Quantity || 1) * (item.unitPrice || item.UnitPrice || 0)) - (item.discount || item.Discount || 0)),
            taxAmount: item.taxAmount || item.TaxAmount || 0,
            total: item.totalAmount || item.TotalAmount || ((((item.quantity || item.Quantity || 1) * (item.unitPrice || item.UnitPrice || 0)) - (item.discount || item.Discount || 0)) + (item.taxAmount || item.TaxAmount || 0))
          })),
          subtotal: invoice.subtotalAmount || invoice.SubtotalAmount || invoice.totalAmount || invoice.TotalAmount || 0,
          totalTaxAmount: invoice.totalTaxAmount || invoice.TotalTaxAmount || invoice.taxAmount || invoice.TaxAmount || 0,
          discount: invoice.discountAmount || invoice.DiscountAmount || 0,
          total: invoice.totalAmount || invoice.TotalAmount || 0,
          notes: invoice.notes || invoice.Notes || '',
          createdAt: invoice.createdAt || invoice.CreatedAt,
          updatedAt: invoice.updatedAt || invoice.UpdatedAt
        };
        
        setBilling(billingData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching billing data:', error);
        setError('Failed to load billing data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBillingData();
  }, [id]);
  
  const handleEditBilling = () => {
    navigate(`/billing/${id}/edit`);
  };
  
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteBilling = async () => {
    try {
      await BillingService.deleteInvoice(id);
      
      handleDeleteDialogClose();
      navigate('/billing');
    } catch (error) {
      console.error('Error deleting billing record:', error);
      alert('Failed to delete billing record. Please try again.');
      handleDeleteDialogClose();
    }
  };
  
  const handlePrintInvoice = async () => {
    // Create a printable version of the invoice
    const printContent = document.getElementById('invoice-content');
    
    if (!printContent) {
      console.error('Print content element not found');
      return;
    }
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      try {
        window.print();
      } catch (e) {
        alert('Please allow pop-ups to print the invoice');
      }
      return;
    }
    
    let companyName = 'Clinic Management';
    let logoDataUrl = null;
    try {
      const settingsRes = await ClinicSettingsService.getSettings();
      const settings = settingsRes?.data ?? settingsRes;
      companyName = settings?.companyName ?? settings?.CompanyName ?? companyName;
      logoDataUrl = settings?.logoDataUrl ?? settings?.LogoDataUrl ?? null;
    } catch {
    }

    // Add print-specific styles
    const printStyles = `
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #000;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
        }
        .invoice-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .company-logo {
          width: 56px;
          height: 56px;
          object-fit: contain;
        }
        .company-name {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 2px;
        }
        .invoice-title {
          font-size: 24px;
          font-weight: bold;
        }
        .invoice-details {
          margin-bottom: 20px;
        }
        .invoice-details div {
          margin-bottom: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
        }
        .text-right {
          text-align: right;
        }
        .total-row {
          font-weight: bold;
        }
        .notes {
          margin-top: 30px;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      </style>
    `;

    const showTaxColumn =
      billing.totalTaxAmount > 0 || billing.items.some(item => (item.taxAmount || 0) > 0);
    const showDiscountColumn = billing.discount > 0 || billing.items.some(item => (item.discount || 0) > 0);
    const columnCount = 5 + (showDiscountColumn ? 1 : 0) + (showTaxColumn ? 1 : 0);
    const summaryEmptyColSpan = Math.max(1, columnCount - 2);

    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // Create the print content HTML
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${billing.invoiceNumber}</title>
          ${printStyles}
        </head>
        <body>
          <div class="invoice-header">
            <div class="invoice-brand">
              ${logoDataUrl ? `<img class="company-logo" src="${logoDataUrl}" alt="Logo" />` : ''}
              <div>
                <div class="company-name">${escapeHtml(companyName)}</div>
                <div class="invoice-title">INVOICE</div>
              </div>
            </div>
            <div>
              <div><strong>Invoice #:</strong> ${billing.invoiceNumber}</div>
              <div><strong>Date:</strong> ${billing.date ? billing.date.toLocaleDateString() : ''}</div>
              <div><strong>Status:</strong> ${billing.status}</div>
            </div>
          </div>
          
          <div class="invoice-details">
            <div><strong>Patient:</strong> ${billing.patientName}</div>
            <div><strong>Description:</strong> ${billing.description}</div>
            ${billing.paymentMethod ? `<div><strong>Payment Method:</strong> ${billing.paymentMethod}</div>` : ''}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Unit Price</th>
                ${showDiscountColumn ? '<th class="text-right">Discount</th>' : ''}
                <th class="text-right">Subtotal</th>
                ${showTaxColumn ? '<th class="text-right">Tax</th>' : ''}
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${billing.items.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td class="text-right">${item.quantity}</td>
                  <td class="text-right">${formatCurrency(item.unitPrice)}</td>
                  ${showDiscountColumn ? `<td class="text-right">${(item.discount || 0) > 0 ? formatCurrency(item.discount || 0) : '-'}</td>` : ''}
                  <td class="text-right">${formatCurrency(item.subtotal || 0)}</td>
                  ${showTaxColumn ? `<td class="text-right">${(item.taxAmount || 0) > 0 ? formatCurrency(item.taxAmount || 0) : '-'}</td>` : ''}
                  <td class="text-right">${formatCurrency(item.total)}</td>
                </tr>
              `).join('')}
              ${showTaxColumn ? `
                <tr>
                  <td colspan="${summaryEmptyColSpan}"></td>
                  <td class="text-right">Tax</td>
                  <td class="text-right">${formatCurrency(billing.totalTaxAmount)}</td>
                </tr>
              ` : ''}
              ${billing.discount > 0 ? `
                <tr>
                  <td colspan="${summaryEmptyColSpan}"></td>
                  <td class="text-right">Discount</td>
                  <td class="text-right">-${formatCurrency(billing.discount)}</td>
                </tr>
              ` : ''}
              <tr class="total-row">
                <td colspan="${summaryEmptyColSpan}"></td>
                <td class="text-right">Total</td>
                <td class="text-right">${formatCurrency(billing.total)}</td>
              </tr>
            </tbody>
          </table>
          
          ${billing.notes ? `
            <div class="notes">
              <strong>Notes:</strong>
              <p>${billing.notes}</p>
            </div>
          ` : ''}
        </body>
      </html>
    `;
    
    // Write the HTML to the new window and print
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
        // Close the window after printing (optional)
        // printWindow.close();
      }, 250);
    };
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Overdue':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  if (!billing) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Billing record not found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          component={RouterLink}
          to="/billing"
        >
          Back to Billing
        </Button>
      </Box>
    );
  }

  const showDiscountColumn =
    (Number(billing.discount || 0) > 0) ||
    (Array.isArray(billing.items) ? billing.items : []).some(i => Number(i.discount || 0) > 0);

  const showTaxApplied =
    (Number(billing.totalTaxAmount || 0) > 0) ||
    (Array.isArray(billing.items) ? billing.items : []).some(item => Number(item.taxAmount || 0) > 0);
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/billing"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Invoice {billing.invoiceNumber}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{ mr: 1 }}
            onClick={handlePrintInvoice}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{ mr: 1 }}
            onClick={handleEditBilling}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteDialogOpen}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3 }} id="invoice-content">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  {billing.description}
                </Typography>
                <Chip 
                  label={billing.status}
                  color={getStatusColor(billing.status)}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Patient
                  </Typography>
                  <Typography variant="body1">
                    <Button 
                      component={RouterLink} 
                      to={`/patients/${billing.patientId}`}
                      sx={{ p: 0, textTransform: 'none' }}
                    >
                      {billing.patientName}
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {billing.date ? billing.date.toLocaleDateString() : '-'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1">
                    {billing.invoiceNumber}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {billing.paymentMethod || 'Not paid yet'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Invoice Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      {showDiscountColumn && (
                        <TableCell align="right">Discount</TableCell>
                      )}
                      <TableCell align="right">Subtotal</TableCell>
                      <TableCell align="right">Tax</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billing.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        {showDiscountColumn && (
                          <TableCell align="right">
                            {(item.discount || 0) > 0 ? formatCurrency(item.discount || 0) : '-'}
                          </TableCell>
                        )}
                        <TableCell align="right">{formatCurrency(item.subtotal)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.taxAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                      </TableRow>
                    ))}
                    {showTaxApplied && (
                      <TableRow>
                        <TableCell colSpan={showDiscountColumn ? 5 : 4} />
                        <TableCell align="right">Tax</TableCell>
                        <TableCell align="right">{formatCurrency(billing.totalTaxAmount)}</TableCell>
                      </TableRow>
                    )}
                    {billing.discount > 0 && (
                      <TableRow>
                        <TableCell colSpan={showDiscountColumn ? 5 : 4} />
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">-{formatCurrency(billing.discount)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell colSpan={showDiscountColumn ? 5 : 4} />
                      <TableCell align="right">
                        <Typography variant="h6">Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">{formatCurrency(billing.total)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            
            {billing.notes && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {billing.notes}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(billing.createdAt).toLocaleString()}
          {billing.updatedAt !== billing.createdAt && 
            ` | Last updated: ${new Date(billing.updatedAt).toLocaleString()}`}
        </Typography>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this invoice? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteBilling} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingDetails;
